// ComposerVideoDetails.tsx — the attached clip's Title, Category and Short switch
// (Videos page owner decisions, 2026-09-26):
//   * Title — optional; untitled videos show the post's first line.
//   * Category — the uploader picks; pre-filled from the topic engine's guess on the post
//     text until the uploader touches it.
//   * Short — only clips of 60 s or less; pre-ticked for a portrait or square clip when the
//     member's "Automatic Short Classifier" setting is on (the default), and always the
//     uploader's call (a landscape clip can still be ticked by hand).
//   * Permissions (1.27.60) — Allow downloads (off by default), Allow embedding and Allow
//     clips (on by default). All three can be changed after posting from the watch page.
import styled from 'styled-components';
import { Switch } from '@/ui';
import type { VideoCategoryId } from '@/core/models/post';
import { VIDEO_CATEGORIES, type VideoDetails, type VideoPermissions } from './useVideoDetails';

const Box = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

const Field = styled.input`
  flex: 1 1 220px;
  min-width: 0;
  padding: 7px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 1px;
  }
`;

const Select = styled.select`
  padding: 7px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 1px;
  }
`;

const ShortLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Hint = styled.span`
  font: 400 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface ComposerVideoDetailsProps {
  details: VideoDetails;
  canBeShort: boolean;
  onTitle: (title: string) => void;
  onCategory: (category: VideoCategoryId | '') => void;
  onShort: (next: boolean) => void;
  onPermission: (name: keyof VideoPermissions, value: boolean) => void;
  disabled?: boolean;
}

const PERMISSIONS: { name: keyof VideoPermissions; label: string }[] = [
  { name: 'allowDownload', label: 'Allow downloads' },
  { name: 'allowEmbed', label: 'Allow embedding' },
  { name: 'allowClips', label: 'Allow clips' },
];

export function ComposerVideoDetails({
  details,
  canBeShort,
  onTitle,
  onCategory,
  onShort,
  onPermission,
  disabled,
}: ComposerVideoDetailsProps) {
  return (
    <Box data-testid="composer-video-details">
      <Row>
        <Field
          type="text"
          value={details.title}
          maxLength={100}
          placeholder="Title (optional)"
          aria-label="Video title"
          disabled={disabled}
          onChange={(e) => onTitle(e.target.value)}
        />
        <Select
          value={details.category}
          aria-label="Video category"
          disabled={disabled}
          onChange={(e) => onCategory(e.target.value as VideoCategoryId | '')}
        >
          <option value="">No category</option>
          {VIDEO_CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </Select>
      </Row>
      {canBeShort && (
        <Row>
          <ShortLabel>
            Post as a Flash
            <Switch
              checked={details.short}
              onChange={onShort}
              label="Post as a Flash"
              disabled={disabled}
            />
          </ShortLabel>
          <Hint>Flashes appear in the Flash row and play in the Flash player.</Hint>
        </Row>
      )}
      <Row role="group" aria-label="What viewers may do">
        {PERMISSIONS.map((p) => (
          <ShortLabel key={p.name}>
            {p.label}
            <Switch
              checked={details[p.name]}
              onChange={(v) => onPermission(p.name, v)}
              label={p.label}
              disabled={disabled}
            />
          </ShortLabel>
        ))}
      </Row>
    </Box>
  );
}
